import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { InvalidDataError } from 'app/services/database/invalidDataError';
import { ObjectNotFoundError } from 'app/services/database/objectNotFoundError';
import { PermissionDeniedError } from 'app/services/database/permissionDeniedError';
import { Observable } from 'rxjs';

export type EventsName = 'child_changed' | 'child_removed' | 'value';

@Injectable()
export class DatabaseService {
    constructor(private db: AngularFireDatabase) { }

    public async exists(path: string, key: string) {
        return new Promise<boolean>((resolve, reject) => {
            this.db.database.ref(path).child(key).once('value', val => {
                resolve(val.exists());
            },
                fail => {
                    reject(this.wrapError(fail));
                });
        });
    }

    public async get<T>(path: string, key: string): Promise<T> {
        return new Promise<T>((resolve, reject) => {

            this.db.database.ref(path).child(key).once('value', val => {
                if (!val.exists()) {
                    reject(new ObjectNotFoundError());
                }
                resolve(val.val());
            },
                fail => {
                    reject(this.wrapError(fail));
                });
        });
    }

    public async update<T>(path: string, key: string, value: T) {
        try {
            let ref = this.db.database.ref(path);
            if (key) ref = ref.child(key);
            await ref.update(value);
        } catch (err) {
            throw this.wrapError(err);
        }
    }

    public async push<T>(path?: string, value?: T) {
        try {
            return await this.db.database.ref(path).push(value);
        } catch (err) {
            throw this.wrapError(err);
        }
    }

    public async set<T>(path: string, key: string, value: T) {
        try {
            let ref = this.db.database.ref(path);
            if (key !== undefined) {
                ref = ref.child(key);
            }
            await ref.set(value);
        } catch (err) {
            throw this.wrapError(err);
        }
    }

    public async remove<T>(path: string, key: string) {
        try {
            let ref = this.db.database.ref(path);
            if (key) ref = ref.child(key);
            await ref.remove();
        } catch (err) {
            throw this.wrapError(err);
        }
    }

    public watch<T>(eventName: EventsName, path: string, key: string) {
        return new Observable<T>(obs => {
            let ref = this.db.database.ref(path).child(key);
            let cb = (snapshot: firebase.database.DataSnapshot) => {
                if (snapshot.exists()) {
                    obs.next(snapshot.val());
                } else {
                    obs.next(undefined);
                }
            };

            // Setup
            ref.on(eventName, cb, (cancel: any) => obs.error(cancel));

            // Tear down method on unsubscribe. This cleanup the ref on firebase so we
            // don't receive any more callbacks from the event
            obs.add(() => {
                ref.off(eventName, cb);
            });
        });
    }

    public watchOnce<T>(eventName: EventsName, path: string, key: string) {
        return new Promise<T>((resolve, reject) => {
            let ref = this.db.database.ref(path).child(key);

            ref.once(eventName, (snapshot: firebase.database.DataSnapshot) => {
                if (snapshot.exists()) {
                    resolve(snapshot.val());
                } else {
                    resolve(undefined);
                }
            }, (error: any) => reject(error));
        });
    }

    private wrapError(err) {
        if (err.code === 'PERMISSION_DENIED') {
            return new PermissionDeniedError();
        } else if (err.message.indexOf('contains undefined')) {
            return new InvalidDataError();
        } else {
            return err;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/services/database/database.service.ts